<template>
  <div v-if="company.new_billing_enabled">
    <WidgetSkeleton
      v-if="loading"
      :pulse="true"
    />
    <SidebarWidget v-else>

      <template v-slot:title>
        {{ $t('ui.envelopes.envelopesPage.usageWidget.title') }}
      </template>

      <template v-slot:content>
        <EnvelopeUsageWidgetMetricDisplay :usage="usage" />
        <WidgetInfoGrid :gridRows="planInfoGrid" />
      </template>

      <template v-slot:cta>
        <div v-if="userHasRole('super-admin')">
          <ButtonLink
            v-if="usage.plan.id === 'pay-as-you-go'"
            class="w-full mb-base"
            :to="{ path: 'company-settings/billing', query: { 'add_credits_modal': 'true' }}"
            data-ec="envelope_usage_widget"
            data-et="add_payg_credits_button"
          >
            <span>{{ $t('ui.envelopes.envelopesPage.usageWidget.buyCredits') }}</span>
          </ButtonLink>
          <ButtonLink
            to="company-settings/billing"
            class="w-full"
            data-ec="envelope_usage_widget"
            data-et="manage_plan_button"
          >
            <span>{{ $t('ui.envelopes.envelopesPage.usageWidget.buttonText') }}</span>
          </ButtonLink>
        </div>
        <div
          v-else
          class="text-center"
        >
          <p v-if="usage.plan.id === 'pay-as-you-go'">{{ $t('ui.envelopes.envelopesPage.usageWidget.contactAdmin.payg') }}</p>
          <p v-else>{{ $t('ui.envelopes.envelopesPage.usageWidget.contactAdmin.plan') }}</p>
        </div>
      </template>

    </SidebarWidget>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Moment from 'moment';
import { useWindowFocus } from '@vueuse/core';

import EnvelopeUsageWidgetMetricDisplay from '@/components/ui/envelopes/EnvelopeUsageWidgetMetricDisplay.vue';
import WidgetInfoGrid from '@/components/library/widgets/WidgetInfoGrid.vue';
import SidebarWidget from '@/components/library/widgets/SidebarWidget.vue';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import WidgetSkeleton from '@/components/library/skeletons/WidgetSkeleton.vue';

export default {
  name: 'EnvelopeUsageWidget',
  components: {
    EnvelopeUsageWidgetMetricDisplay,
    SidebarWidget,
    ButtonLink,
    WidgetInfoGrid,
    WidgetSkeleton,
  },
  setup() {
    const focused = useWindowFocus();

    return { focused };
  },
  async created() {
    if (this.company.new_billing_enabled) {
      this.loading = true;

      const getters = [];

      getters.push(this.$store.dispatch('plans/GET_CURRENT_PLAN'));
      getters.push(this.$store.dispatch('metering/GET_USAGE'));

      await Promise.all(getters);

      this.setRecurringWidgetRefresh();
      this.loading = false;
    }
  },
  destroyed() {
    clearInterval(this.initialRefreshInterval);
    clearInterval(this.continuousRefreshInterval);
  },
  data() {
    return {
      continuousRefreshInterval: '',
      initialRefreshInterval: '',
      numberOfRefreshes: 0,
      showCreditsModal: false,
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      usage: 'metering/usage',
      company: 'company/company',
      userHasRole: 'roles/userHasRole',
    }),
    planInfoGrid() {
      let planInfoGrid = [
        {
          title: this.$t('ui.envelopes.envelopesPage.usageWidget.yourPlan'),
          result: this.usage.plan.nickname,
        },
      ];
      if (this.usage.plan.id !== 'pay-as-you-go') {
        const planEnds = Moment.unix(this.usage.allowance_period.end);
        const daysUntilPlanEnds = planEnds.diff(Moment(), 'days');
        planInfoGrid = [
          ...planInfoGrid,
          {
            title: this.$t('ui.envelopes.envelopesPage.usageWidget.envelopesSent'),
            result: this.$t('ui.envelopes.envelopesPage.usageWidget.xOfY', { x: this.usage.usage, y: this.usage.plan.allowance }),
            danger: this.planEmpty,
          },
          {
            title: this.usage.plan.id === 'free-trial' ? this.$t('ui.envelopes.envelopesPage.usageWidget.freeTrialEndsIn') : this.$t('ui.envelopes.envelopesPage.usageWidget.allowanceResetsIn'),
            result: this.$t('ui.envelopes.envelopesPage.usageWidget.xDays', { x: daysUntilPlanEnds }),
            danger: this.planEmpty,
          },
        ];
      }
      return planInfoGrid;
    },
    planEmpty() {
      return this.usage.plan.allowance <= this.usage.usage;
    },
  },
  methods: {
    setRecurringWidgetRefresh() {
      this.initialRefreshInterval = setInterval(() => {
        this.numberOfRefreshes += 1;
        if (this.numberOfRefreshes === 7) {
          clearInterval(this.initialRefreshInterval);
        }
        if (this.focused) {
          this.$store.dispatch('metering/GET_USAGE');
        }
      }, 15000);
      this.continuousRefreshInterval = setInterval(() => {
        if (this.focused) {
          this.$store.dispatch('metering/GET_USAGE');
        }
      }, 180000);
    },
  },
};
</script>

